/**
 * Phase-5 worker identity hooks. Mirrors the shape of
 * `use-capability-acls.ts` and talks to the admin-ui BFF at
 * `/api/workers/*`, which proxies to the auth-server's
 * `/admin/workers` lifecycle endpoints.
 *
 * NO NATS vocabulary surfaces here — callers see `workerId`,
 * `clientId`, `clientSecret` (only on create/rotate), and status.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

const LOG_PREFIX = '[use-workers]';

export type WorkerStatus = 'active' | 'paused' | 'revoked';
export type WorkerType = 'platform' | 'self_hosted' | 'third_party';

export interface WorkerIdentity {
   id: string;
   name: string;
   description?: string | null;
   worker_type: WorkerType;
   status: WorkerStatus;
   allowed_capability_ids: string[];
   last_secret_rotated_at: string | null;
   last_connected_at: string | null;
   created: string;
   paused_at: string | null;
   revoked_at: string | null;
}

export interface WorkersListResponse {
   workers: WorkerIdentity[];
}

export interface WorkerSecretReveal {
   worker: WorkerIdentity;
   client_id: string;
   client_secret: string;
   issued_at?: string;
   rotated_at?: string;
}

const QUERY_KEY = ['admin-workers'] as const;

export function useWorkers(enabled: boolean = true) {
   return useQuery<WorkersListResponse>({
      enabled,
      queryKey: QUERY_KEY,
      queryFn: async () => {
         const r = await api.get('/workers');
         return r.data as WorkersListResponse;
      },
   });
}

export interface CreateWorkerBody {
   name: string;
   description?: string;
   worker_type: WorkerType;
   allowed_capability_ids?: string[];
}

export function useCreateWorker() {
   const qc = useQueryClient();
   return useMutation({
      mutationFn: async (body: CreateWorkerBody) => {
         console.log(`${LOG_PREFIX}.useCreateWorker.mutationFn - creating`, {
            name: body.name,
            workerType: body.worker_type,
         });
         const r = await api.post('/workers', body);
         return r.data as WorkerSecretReveal;
      },
      onSuccess: () => {
         qc.invalidateQueries({ queryKey: QUERY_KEY });
      },
   });
}

export function useRotateWorkerSecret() {
   const qc = useQueryClient();
   return useMutation({
      mutationFn: async (workerId: string) => {
         console.log(`${LOG_PREFIX}.useRotateWorkerSecret.mutationFn - rotating`, { workerId });
         const r = await api.post(`/workers/${workerId}/rotate-secret`, {});
         return r.data as WorkerSecretReveal;
      },
      onSuccess: () => {
         qc.invalidateQueries({ queryKey: QUERY_KEY });
      },
   });
}

export function usePauseWorker() {
   const qc = useQueryClient();
   return useMutation({
      mutationFn: async (workerId: string) => {
         console.log(`${LOG_PREFIX}.usePauseWorker.mutationFn - pausing`, { workerId });
         const r = await api.post(`/workers/${workerId}/pause`, {});
         return (r.data as { worker: WorkerIdentity }).worker;
      },
      onSuccess: () => {
         qc.invalidateQueries({ queryKey: QUERY_KEY });
      },
   });
}

export function useRevokeWorker() {
   const qc = useQueryClient();
   return useMutation({
      mutationFn: async (workerId: string) => {
         console.log(`${LOG_PREFIX}.useRevokeWorker.mutationFn - revoking`, { workerId });
         const r = await api.post(`/workers/${workerId}/revoke`, {});
         return (r.data as { worker: WorkerIdentity }).worker;
      },
      onSuccess: () => {
         qc.invalidateQueries({ queryKey: QUERY_KEY });
      },
   });
}
